import { useMemo, useState } from 'react'

import { formatMonth } from '@/shared/format'
import { useLocalization } from '@/shared/i18n'
import { Chip, SearchSheet, Stack } from '@/shared/ui'
import type { SheetItem } from '@/shared/ui'

import type { AnswerProps } from './types'

const YEARS_BACK = 30

const RECENT_YEARS = 3

const MONTH_LENGTH = 7

type Open = 'year' | 'month' | null

function limitOf(bound: number | string | undefined): string | undefined {
  return typeof bound === 'string' ? bound.slice(0, MONTH_LENGTH) : undefined
}

function monthOf(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`
}

function lastMonthOf(max: string | undefined): string {
  return max ?? monthOf(new Date().getFullYear(), 11)
}

export function MonthAnswer({ question, value, onChange }: AnswerProps) {
  const { t, language } = useLocalization()
  const [open, setOpen] = useState<Open>(null)
  const [year, setYear] = useState<number>(() => new Date().getFullYear())

  const min = limitOf(question.min)
  const last = lastMonthOf(limitOf(question.max))
  const lastYear = Number(last.slice(0, 4))
  const firstYear = min ? Number(min.slice(0, 4)) : lastYear - YEARS_BACK

  const chosen = typeof value === 'string' && value !== '' ? value.slice(0, MONTH_LENGTH) : ''

  const years = useMemo<SheetItem[]>(() => {
    const items: SheetItem[] = []

    for (let back = lastYear; back >= firstYear; back -= 1) {
      items.push({ value: String(back), name: String(back) })
    }

    return items
  }, [firstYear, lastYear])

  const months = useMemo<SheetItem[]>(() => {
    const items: SheetItem[] = []

    for (let month = 11; month >= 0; month -= 1) {
      const candidate = monthOf(year, month)

      if (candidate > last || (min !== undefined && candidate < min)) {
        continue
      }

      items.push({
        value: candidate,
        name: formatMonth(`${candidate}-01`, language),
      })
    }

    return items
  }, [year, last, min, language])

  const recent = years.slice(0, RECENT_YEARS).map((item) => Number(item.value))

  function pickYear(picked: number) {
    setYear(picked)
    setOpen('month')
  }

  return (
    <>
      <Stack direction="row" gap="sm" wrap className="ask__options">
        {chosen ? (
          <Chip selected onClick={() => setOpen('year')}>
            {formatMonth(`${chosen}-01`, language)}
          </Chip>
        ) : (
          recent.map((option) => (
            <Chip key={option} onClick={() => pickYear(option)}>
              {option}
            </Chip>
          ))
        )}
        <Chip onClick={() => setOpen('year')}>{t('survey.month.earlier')}</Chip>
      </Stack>

      {open === 'year' ? (
        <SearchSheet
          title={question.text}
          placeholder={t('survey.month.year.placeholder')}
          items={years}
          chosen={chosen ? [chosen.slice(0, 4)] : []}
          onPick={(picked) => pickYear(Number(picked))}
          onClose={() => setOpen(null)}
        />
      ) : null}

      {open === 'month' ? (
        <SearchSheet
          title={t('survey.month.month', { year })}
          placeholder={t('survey.month.month.placeholder')}
          items={months}
          chosen={chosen ? [chosen] : []}
          onPick={(picked) => {
            onChange(picked)
            setOpen(null)
          }}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  )
}
